import { apiClient, handleApiError } from '@/lib/api-client';
import type {
  AccountHit,
  AdminAutomation,
  AdminAutomationFilters,
  AdminAutomationRun,
  AdminAutomationSummary,
  AdminAutomationUpdate,
  AdminCreateBody,
  AdminRunFilters,
  AdminSettings,
  AutomationRun,
  CustomerAutomation,
  CustomerAutomationDetail,
  EstimateResponse,
  Granularity,
  Paged,
  PromptHistoryEntry,
  RefineResponse,
  SpendingResponse,
  TestEventResponse,
  TestRunResponse,
} from '@/types/automations';

/** The operator-editable part of an automation, in the shape PUT expects. */
export function toAutomationUpdate(a: AdminAutomation): AdminAutomationUpdate {
  return {
    name: a.name,
    description: a.description ?? '',
    client_id: a.client_id,
    model: a.model,
    inputs: a.inputs ?? [],
    brief: a.brief ?? '',
    prompt: a.prompt ?? '',
    output_fields: a.output_fields ?? [],
    tools: a.tools,
    image: a.image,
    klaviyo: { metric_name: a.klaviyo?.metric_name ?? '', profile_email_field: a.klaviyo?.profile_email_field ?? '' },
    callback_url: a.callback_url ?? '',
    price_tokens: a.price_tokens ?? 0,
    published: a.published,
  };
}

/** Drop an empty Klaviyo key so saving keeps the stored one. */
export function forSave(d: AdminAutomationUpdate): AdminAutomationUpdate {
  const { klaviyo_api_key, ...rest } = d;
  return klaviyo_api_key && klaviyo_api_key.trim() ? { ...rest, klaviyo_api_key: klaviyo_api_key.trim() } : rest;
}

const CUSTOMER = '/api/v1/automations';
const ADMIN = '/api/v1/admin';

// LLM-backed admin calls take far longer than the default 10 s client timeout.
const LLM_TIMEOUT = { timeout: 120_000 };

/**
 * Paginated list endpoints (/admin/automations, /admin/runs, customer runs) answer
 * `{items, total, page, limit}`; the rest (/admin/accounts, customer list, history) are bare arrays.
 */
function toPaged<T>(d: Partial<Paged<T>> | null | undefined, page: number, limit: number): Paged<T> {
  return {
    items: Array.isArray(d?.items) ? d.items : [],
    total: typeof d?.total === 'number' ? d.total : null,
    page: typeof d?.page === 'number' ? d.page : page,
    limit: typeof d?.limit === 'number' ? d.limit : limit,
  };
}

async function call<T>(fn: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await fn()).data;
  } catch (e) {
    throw handleApiError(e);
  }
}

class AutomationsService {
  // ---- customer ----
  listMine(): Promise<CustomerAutomation[]> {
    return call(() => apiClient.get<CustomerAutomation[]>(CUSTOMER));
  }

  getMine(id: string): Promise<CustomerAutomationDetail> {
    return call(() => apiClient.get<CustomerAutomationDetail>(`${CUSTOMER}/${id}`));
  }

  setEnabled(id: string, enabled: boolean): Promise<CustomerAutomationDetail> {
    return call(() => apiClient.patch<CustomerAutomationDetail>(`${CUSTOMER}/${id}`, { enabled }));
  }

  async listMyRuns(id: string, page = 1, limit = 20): Promise<Paged<AutomationRun>> {
    const data = await call(() =>
      apiClient.get<Paged<AutomationRun>>(`${CUSTOMER}/${id}/runs`, { params: { page, limit } }),
    );
    return toPaged(data, page, limit);
  }

  getSpending(id: string, granularity: Granularity, page = 1, limit = 50): Promise<SpendingResponse> {
    return call(() =>
      apiClient.get<SpendingResponse>(`${CUSTOMER}/${id}/spending`, { params: { granularity, page, limit } }),
    );
  }

  extendImages(id: string): Promise<{ extended: number }> {
    return call(() => apiClient.post<{ extended: number }>(`${CUSTOMER}/${id}/images/extend`));
  }

  async hasAny(): Promise<boolean> {
    const r = await call(() => apiClient.get<{ has_any: boolean }>(`${CUSTOMER}/has-any`));
    return r.has_any === true;
  }

  // ---- admin ----
  searchAccounts(q: string): Promise<AccountHit[]> {
    return call(() => apiClient.get<AccountHit[]>(`${ADMIN}/accounts`, { params: { q } }));
  }

  async adminList(f: AdminAutomationFilters = {}): Promise<Paged<AdminAutomationSummary>> {
    const params = { ...f, type: f.type || undefined };
    const data = await call(() => apiClient.get<Paged<AdminAutomationSummary>>(`${ADMIN}/automations`, { params }));
    return toPaged(data, f.page ?? 1, f.limit ?? 20);
  }

  adminGet(id: string): Promise<AdminAutomation> {
    return call(() => apiClient.get<AdminAutomation>(`${ADMIN}/automations/${id}`));
  }

  adminCreate(body: AdminCreateBody): Promise<AdminAutomation> {
    return call(() => apiClient.post<AdminAutomation>(`${ADMIN}/automations`, body));
  }

  adminUpdate(id: string, body: AdminAutomationUpdate): Promise<AdminAutomation> {
    return call(() => apiClient.put<AdminAutomation>(`${ADMIN}/automations/${id}`, body));
  }

  adminDuplicate(id: string, clientId: string): Promise<AdminAutomation> {
    return call(() => apiClient.post<AdminAutomation>(`${ADMIN}/automations/${id}/duplicate`, { client_id: clientId }));
  }

  async adminDelete(id: string): Promise<void> {
    await call(() => apiClient.delete(`${ADMIN}/automations/${id}`));
  }

  regenerateToken(id: string): Promise<AdminAutomation> {
    return call(() => apiClient.post<AdminAutomation>(`${ADMIN}/automations/${id}/regenerate-token`));
  }

  regenerateSecret(id: string): Promise<AdminAutomation> {
    return call(() => apiClient.post<AdminAutomation>(`${ADMIN}/automations/${id}/regenerate-secret`));
  }

  importKlaviyoKey(id: string): Promise<AdminAutomation> {
    return call(() => apiClient.post<AdminAutomation>(`${ADMIN}/automations/${id}/klaviyo/import-account-key`));
  }

  refine(id: string, brief: string): Promise<RefineResponse> {
    return call(() => apiClient.post<RefineResponse>(`${ADMIN}/automations/${id}/refine`, { brief }, LLM_TIMEOUT));
  }

  history(id: string): Promise<PromptHistoryEntry[]> {
    return call(() => apiClient.get<PromptHistoryEntry[]>(`${ADMIN}/automations/${id}/history`));
  }

  test(id: string, row: Record<string, unknown>, automation?: AdminAutomationUpdate): Promise<TestRunResponse> {
    return call(() =>
      apiClient.post<TestRunResponse>(`${ADMIN}/automations/${id}/test`, { row, automation }, LLM_TIMEOUT),
    );
  }

  estimate(id: string, runs: number, samples?: Record<string, unknown>[]): Promise<EstimateResponse> {
    return call(() =>
      apiClient.post<EstimateResponse>(`${ADMIN}/automations/${id}/estimate`, { runs, samples }, { timeout: 170_000 }),
    );
  }

  sendTestEvent(id: string, row: Record<string, unknown>, profileEmail: string): Promise<TestEventResponse> {
    return call(() =>
      apiClient.post<TestEventResponse>(
        `${ADMIN}/automations/${id}/klaviyo/test-event`,
        { row, profile_email: profileEmail },
        LLM_TIMEOUT,
      ),
    );
  }

  async listRuns(f: AdminRunFilters = {}): Promise<Paged<AdminAutomationRun>> {
    const params = { ...f, type: f.type || undefined, status: f.status || undefined, mode: f.mode || undefined };
    const data = await call(() => apiClient.get<Paged<AdminAutomationRun>>(`${ADMIN}/runs`, { params }));
    return toPaged(data, f.page ?? 1, f.limit ?? 20);
  }

  getRun(runId: string): Promise<AdminAutomationRun> {
    return call(() => apiClient.get<AdminAutomationRun>(`${ADMIN}/runs/${runId}`));
  }

  getSettings(): Promise<AdminSettings> {
    return call(() => apiClient.get<AdminSettings>(`${ADMIN}/settings`));
  }

  putSettings(body: AdminSettings): Promise<AdminSettings> {
    return call(() => apiClient.put<AdminSettings>(`${ADMIN}/settings`, body));
  }
}

export const automationsService = new AutomationsService();
